import React, { FunctionComponent, useState } from 'react'
import { connect } from 'react-redux'
import {
  goToCoverageDetailsSection,
  setCreditSessionFinished,
  setRefiAppCreditStepState,
  setRefiAppRejectedCreditAppState,
} from '../refiActions'
import {
  createProductPackage,
  updateProductPackage,
} from '../productPackageActions'
import { findOrCreateVehicle } from '../refiActions'
import { formValueSelector } from 'redux-form'
import { Callback } from '../../../utils/types'
import {
  selectCurrentRefiVehicle,
  selectCurrentRefiUser,
  selectCurrentProductPackageId,
  selectProductPackage,
  selectCurrentRefiApplication,
} from '../../../reducers'
import { User } from '../../../core/types'
import { displayNotification } from '../../../actions/notificationsActions'
import {
  resetCoverageDetailsState,
  updateSelectedOption,
} from '../coverageActions'

const getPreviouslySelectedPlan = (state, productPackageId) => {
  const coverageState = state?.refis?.applicationCoverageState || {}
  const selectedOptionFromState = coverageState?.selectedOptions?.[0]

  if (selectedOptionFromState?.productId && selectedOptionFromState?.id) {
    return {
      productId: String(selectedOptionFromState.productId),
      optionId: selectedOptionFromState.id,
    }
  }

  const currentPackage = selectProductPackage(
    state.productPackages,
    productPackageId
  )
  const selectedProduct = (currentPackage?.products || []).find(
    (product) => !!product.selectedProductOptionId
  )

  if (selectedProduct?.id && selectedProduct?.selectedProductOptionId) {
    return {
      productId: String(selectedProduct.id),
      optionId: selectedProduct.selectedProductOptionId,
    }
  }

  return null
}

const reapplySelectedPlanIfPresent = (dispatch, pkg, previousPlan) => {
  if (!pkg || !previousPlan) return

  const matchedProduct = (pkg.products || []).find(
    (product) => String(product.id) === String(previousPlan.productId)
  )

  if (!matchedProduct) return

  const matchedOption = (matchedProduct.options || []).find(
    (option) => String(option.id) === String(previousPlan.optionId)
  )

  if (!matchedOption) return

  dispatch(
    updateSelectedOption({
      ...matchedOption,
      productId: String(matchedProduct.id),
      productDescription: matchedProduct.description,
      productIsNew: matchedProduct.is_new,
    })
  )
}

const shouldRestoreCoverageSection = (applicationState) => {
  return applicationState?.showVehicle || applicationState?.showProfile
}

interface Props {
  setRefiAppCreditStepState: Callback
  onContinueWithoutCredit: Callback
  setRefiAppRejectedCreditAppState: Callback
  user: User
  productPackageId: number
}

export const refreshCoverageProducts = () => async (dispatch, getState) => {
  const state = getState()
  const productPackageId = selectCurrentProductPackageId(state)
  if (!productPackageId) return

  const user = selectCurrentRefiUser(state)
  const vehicle = selectCurrentRefiVehicle(state)
  const previouslySelectedPlan = getPreviouslySelectedPlan(state, productPackageId)

  // flag the package as loading so coverage sections show their loading state
  // while the backend re-rates (GetRate)
  dispatch({ type: 'PRODUCT_PACKAGE_REQUEST' })
  dispatch(resetCoverageDetailsState())
  const pkg = await dispatch(
    updateProductPackage({
      id: productPackageId,
      user_id: user.id,
      vehicle_id: vehicle?.id,
      products: [],
      selected_products: [],
    })
  )

  reapplySelectedPlanIfPresent(dispatch, pkg, previouslySelectedPlan)
}

export const onContinueWithoutCredit =
  (user, productPackageId, history, vehicle, finalTrimChoice) =>
  async (dispatch, getState) => {
    // Submit the Vehicle Details form if it wasn't just submitted (it'll pass the vehicle)
    if (!vehicle) {
      const state = getState()
      const selector = formValueSelector('plateVinForm')
      const {
        plate,
        state: plateState,
        vin,
        year,
        make,
        model,
        trim_id,
        mileage,
        condition,
        transmission,
        used_new,
      } = selector(
        state,
        'plate',
        'state',
        'vin',
        'year',
        'make',
        'model',
        'trim_id',
        'mileage',
        'condition',
        'transmission',
        'used_new'
      )

      const trimId =
        typeof trim_id === 'number' ? trim_id : trim_id && trim_id.trim()

      const payload = {
        plate,
        state: plateState,
        vin: vin && vin.trim() !== '' ? vin.trim() : null,
        year,
        make,
        model,
        trim_id: trimId,
        user_id: user.id,
        product_package_id: productPackageId,
        mileage,
        condition,
        transmission,
        used_new,
      }
      await dispatch(findOrCreateVehicle(payload))
    }

    try {
      // use the updated vehicle.id after the findOrCreateVehicle call
      const updatedVehicle = selectCurrentRefiVehicle(getState())

      // close Vehicle Details and request package creation
      dispatch({
        type: 'SET_APPLICATION_STATE',
        payload: { showVehicle: false, showProfile: false },
      })
      dispatch({ type: 'PRODUCT_PACKAGE_REQUEST' })

      // if productPackageId is present, then update instead of create
      if (productPackageId) {
        const previouslySelectedPlan = getPreviouslySelectedPlan(
          getState(),
          productPackageId
        )
        dispatch(resetCoverageDetailsState())
        const pkg = await dispatch(
          updateProductPackage({
            id: productPackageId,
            user_id: user.id,
            vehicle_id: updatedVehicle?.id,
            products: [],
            selected_products: [],
          })
        )

        reapplySelectedPlanIfPresent(dispatch, pkg, previouslySelectedPlan)

        const currentStatus = getState().refis.applicationState

        // If the VIN for a booked package is being validated
        if (updatedVehicle?.vin) {
          if (pkg.selected_product_id) {
            // Do not finalize here. Finalization happens only when the user
            // clicks "Save and Send" on the payment step (with a VIN present).
            dispatch(goToCoverageDetailsSection())
            return
          }

          dispatch(goToCoverageDetailsSection())
          dispatch(
              displayNotification(
                'VIN validated. Please select another plan for this VIN',
                'error'
              )
          )
          return
        }

        // No products available for this vehicle — redirect to vehicle section with notification
        if (!pkg?.products?.length) {
          dispatch({ type: 'PRODUCTS_NOT_AVAILABLE_ERROR' })
          dispatch(
            displayNotification(
              'No plans available for this vehicle',
              'error',
              3000
            )
          )
          dispatch({
            type: 'SET_APPLICATION_STATE',
            payload: {
              showVehicle: true,
              showProfile: false,
              showUnderwriting: false,
              showCoverage: false,
              showCredit: false,
              creditSectionFinished: false,
            },
          })
          return
        }
      } else {
        // New Deal and Add a Vehicle must not be handed back an existing
        // quoted/selected package on this vehicle. Same vehicle is fine and
        // expected; the deal is what has to be new.
        const { fresh } = selectCurrentRefiApplication(getState())

        await dispatch(
          createProductPackage({
            user_id: user.id,
            vehicle_id: updatedVehicle?.id,
            final_trim_choice: finalTrimChoice,
            ...(fresh ? { fresh: true } : {}),
          })
        )
      }

      // now mark credit finished and reveal Coverage section
      dispatch(setCreditSessionFinished())
      dispatch(goToCoverageDetailsSection())
    } catch (error) {
      dispatch({ type: 'CREATE_PRODUCTPACKAGE_FAILURE' })
      // If insurance API returns 503, require VIN and reopen Vehicle section
      if (error.response?.status === 503) {
        dispatch({ type: 'VIN_REQUIRED_ERROR' })
        dispatch({
          type: 'SET_APPLICATION_STATE',
          payload: {
            showVehicle: true,
            showProfile: false,
            showUnderwriting: false,
            showCoverage: false,
            showCredit: false,
            creditSectionFinished: false,
          },
        })
        return
      }
      throw error
    }

    dispatch(setRefiAppRejectedCreditAppState(true))
  }

export const CreditApplicationChoice: FunctionComponent<Props> = ({
  setRefiAppRejectedCreditAppState,
  setRefiAppCreditStepState,
  onContinueWithoutCredit,
  user,
  productPackageId,
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const onContinueWithCredit = () => {
    setRefiAppRejectedCreditAppState(false)
    setRefiAppCreditStepState('SoftPull')
  }
  return (
    <form className="form">
      <div className="form__paragraph">
        Would you like to get pre-qualified?
      </div>

      <div className="form__group filter__buttons">
        <button
          onClick={onContinueWithCredit}
          type="button"
          data-testid="continue-credit-btn"
          className="button button--primary"
          disabled={process.env.REACT_APP_CREDIT_FLOW_ENABLED !== 'true'}
        >
          Yes, Get Pre-Qualified
        </button>
        <button
          onClick={() => {
            setIsLoading(true)
            onContinueWithoutCredit(user, productPackageId)
          }}
          type="button"
          data-testid="continue-without-credit-btn"
          className="button"
        >
          {isLoading ? 'Loading...' : 'No, Continue without Credit'}
        </button>
      </div>
    </form>
  )
}

export const mapStateToProps = (state) => ({
  user: selectCurrentRefiUser(state),
  productPackageId: selectCurrentProductPackageId(state),
})

export default connect(mapStateToProps, {
  setRefiAppCreditStepState,
  setRefiAppRejectedCreditAppState,
  onContinueWithoutCredit,
})(CreditApplicationChoice)
